import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PaginationListType, getPageParam } from '../../Types/PaginationParams';
import Pagination from './Pagination';
import LoadingSpinner from './LoadingSpinner';
import { RootState, useAppDispatch } from '../../store';
import { setListReloadType } from '../../Store/features/environment/environmentSlice';
import { useAppSelector } from '../../store';
interface PaginationWrapperProps<T> {
    listType: PaginationListType;
    items?: T[] | null;
    itemsPerPage: number;
    renderItem: (item: T, index: number) => React.ReactNode;
    className?: string;
    itemsFunction?: (
        page: number,
        itemsPerPage: number,
        optionalParams?: Record<string, string>,
        query?: string | null,
    ) => Promise<{ data: { data: T[]; total: number } }> | any;

    optionalParams?: Record<string, string>;
    query?: string | null;
    totalItems?: number;
    setTotalItems?: (total: number) => void;
}

export function PaginationWrapper<T>({
    listType,
    items = null,
    itemsPerPage,
    renderItem,
    className = '',
    itemsFunction,
    optionalParams = {},
    query = null,
    totalItems = 0,
    setTotalItems = () => {},
}: PaginationWrapperProps<T>) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [internalItems, setInternalItems] = useState<T[]>([]);
    const pageParam = getPageParam(listType);
    const pageFromUrlRaw = searchParams.get(pageParam);
    const pageFromUrl = pageFromUrlRaw ? Number(pageFromUrlRaw) : undefined;
    const [currentPage, setCurrentPage] = useState(pageFromUrl || 1);
    const demo = useAppSelector((state: RootState) => state.settings.demo);
    const listReloadType = useAppSelector((state: RootState) => state.environment.listReloadType);
    const dispatch = useAppDispatch();
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);

    useEffect(() => {
        if (listReloadType === listType) {
            itemsFunction &&
                itemsFunction(currentPage, itemsPerPage, optionalParams, query ?? null).then(
                    (response: { data: { data: T[]; total: number } }) => {
                        setInternalItems(response.data.data || []);
                        setTotalItems(response.data.total);
                        dispatch(setListReloadType(null));
                        setLoading(false);
                    },
                );
        }
        // eslint-disable-next-line
    }, [listReloadType, currentPage, itemsPerPage, itemsFunction, demo, listType, dispatch]);

    const previousQueryRef = useRef<string | null | undefined>(query);

    useEffect(() => {
        if (!items) {
            itemsFunction &&
                itemsFunction(currentPage, itemsPerPage, optionalParams, query).then(
                    (response: { data: { data: T[]; total: number } }) => {
                        setInternalItems(response.data.data || []);
                        setTotalItems(response.data.total);
                        setLoading(false);
                    },
                );
        } else {
            setInternalItems(items || []);
            setTotalItems(items.length);
            setLoading(false);
        }
        // eslint-disable-next-line
    }, [currentPage, itemsPerPage, itemsFunction, items, demo, query]);

    useEffect(() => {
        if (query !== previousQueryRef.current && previousQueryRef.current !== undefined) {
            setCurrentPage(1);
            setLoading(true);
        }
        previousQueryRef.current = query;
        // eslint-disable-next-line
    }, [query]);

    useEffect(() => {
        if (!pageFromUrlRaw) {
            const params = new URLSearchParams(searchParams.toString());
            params.set(pageParam, '1');
            setSearchParams(params, { replace: true });
        }
        // eslint-disable-next-line
    }, []);

    const changeSourceRef = useRef<'user' | 'url' | null>(null);

    useEffect(() => {
        if (pageFromUrl && pageFromUrl !== currentPage) {
            changeSourceRef.current = 'url';
            setCurrentPage(pageFromUrl);
        }
        // eslint-disable-next-line
    }, [pageFromUrl, listType]);

    const didFirstSyncRef = useRef(false);
    useEffect(() => {
        if (!didFirstSyncRef.current) {
            didFirstSyncRef.current = true;
            return;
        }
        if (changeSourceRef.current !== 'user') {
            return;
        }

        const params = new URLSearchParams(window.location.search);
        const pageParamKey = getPageParam(listType);
        if (currentPage > 1) {
            params.set(pageParamKey, String(currentPage));
        } else {
            params.delete(pageParamKey);
        }

        const newSearch = params.toString();
        const currentSearch = window.location.search.replace(/^\?/, '');
        if (newSearch === currentSearch) {
            changeSourceRef.current = null;
            return;
        }

        setSearchParams(params, { replace: false });
        changeSourceRef.current = null;
    }, [currentPage, setSearchParams, listType]);

    const renderedItems = internalItems.map((item, idx) => renderItem(item, idx));
    const startIdx = (currentPage - 1) * itemsPerPage;
    const endIdx = startIdx + itemsPerPage;
    const pagedItems = items ? renderedItems.slice(startIdx, endIdx) : renderedItems;

    return (
        <div>
            {pagedItems.length === 0 && !loading && query && (
                <div className="flex justify-center">
                    {i18n.translate({
                        id: 'emptyResponse.' + listType,
                        fallback: i18n.translate({ id: 'noDataAvailable', sourceName: 'Pagination' }),
                        sourceName: 'Pagination',
                    })}
                </div>
            )}

            {loading ? (
                <div className="flex justify-center">
                    <LoadingSpinner />
                </div>
            ) : (
                <div className={className} id="pagination-content-area">
                    <>{pagedItems}</>
                </div>
            )}

            {totalItems > itemsPerPage && (
                <>
                    <hr className="border-layout-borderColor mt-5 mb-8" />
                    <Pagination
                        listType={listType}
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        onPageChange={
                            ((newPage: number) => {
                                changeSourceRef.current = 'user';
                                setCurrentPage(newPage);
                            }) as (newPage: number, listType: PaginationListType) => void
                        }
                        totalItems={totalItems}
                    />
                </>
            )}
        </div>
    );
}
